// src/pages/Dashboard/Home.tsx
import React, { JSX, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../store/store";
import { getProfile } from "../../store/slices/userSlice";
import { getBalance, getTransactions } from "../../store/slices/walletSlice";
import Header from "../../components/Layout/Header";
import BottomNav from "../../components/Layout/BottomNav";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import CountryPromptModal, {
  getCountryPromptKey,
} from "../../components/UI/CountryPromptModal";
import globalStyle from "../../styles/globalStyle";
import {
  formatCurrency,
  formatNumber,
  formatDate,
  getXrpPrice,
  setXrpPriceInUSD,
  formatXRP,
} from "../../utils/formatters";
import api from "../../services/api";
import tradingBotService from "../../services/tradingBotService";

import {
  ArrowUpIcon,
  ArrowDownIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowTrendingUpIcon,
  ArrowsRightLeftIcon,
  BanknotesIcon,
  FireIcon,
  InformationCircleIcon,
  CalendarIcon,
  ArrowPathIcon,
  RocketLaunchIcon,
  CpuChipIcon,
  GiftIcon,
  SparklesIcon,
  UserGroupIcon,
  PlusCircleIcon,
  XMarkIcon,
  TrophyIcon,
} from "@heroicons/react/24/outline";

// ── Loading ───────────────────────────────────────────────────────────────────

const LoadingScreen: React.FC<{ message?: string }> = ({
  message = "Loading dashboard...",
}) => (
  <div className="w-full h-64 bg-black">
    <div className="h-full flex flex-col gap-2 items-center justify-center bg-black/80">
      <svg className={`${globalStyle.loadingSpinner} mr-2`} viewBox="0 0 24 24">
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <p className="mt-4 text-gray-400">{message}</p>
    </div>
  </div>
);

// ── Broadcast Marquee ─────────────────────────────────────────────────────────

interface BroadcastData {
  message: string;
  is_active: boolean;
  type: "info" | "warning" | "success" | "danger";
  speed: number;
}

const TYPE_STYLES: Record<
  BroadcastData["type"],
  { bar: string; text: string; dot: string }
> = {
  info: {
    bar: "bg-blue-900   border-b border-blue-700",
    text: "text-blue-200",
    dot: "bg-blue-400",
  },
  warning: {
    bar: "bg-yellow-900 border-b border-yellow-700",
    text: "text-yellow-200",
    dot: "bg-yellow-400",
  },
  success: {
    bar: "bg-green-900  border-b border-green-700",
    text: "text-green-200",
    dot: "bg-green-400",
  },
  danger: {
    bar: "bg-red-900    border-b border-red-700",
    text: "text-red-200",
    dot: "bg-red-400",
  },
};

const BroadcastMarquee: React.FC<{
  data: BroadcastData;
  onDismiss: () => void;
}> = ({ data, onDismiss }) => {
  const styles = TYPE_STYLES[data.type] ?? TYPE_STYLES.info;
  const textPx = data.message.length * 7;
  const totalPx =
    (typeof window !== "undefined" ? window.innerWidth : 1200) + textPx;
  const durationSec = Math.max(4, Math.round(totalPx / data.speed));

  return (
    <div
      className={`relative flex items-center overflow-hidden px-3 py-2 ${styles.bar}`}
      style={{ minHeight: 36 }}
    >
      <div
        className={`flex-shrink-0 w-2 h-2 rounded-full mr-3 animate-pulse ${styles.dot}`}
      />
      <div className="flex-1 overflow-hidden relative" style={{ height: 24 }}>
        <span
          className={`absolute whitespace-nowrap text-sm font-medium ${styles.text}`}
          style={{
            animation: `broadcast-single ${durationSec}s linear infinite`,
          }}
        >
          📢 &nbsp; {data.message}
        </span>
      </div>
      <button
        onClick={onDismiss}
        className="flex-shrink-0 ml-3 text-gray-500 hover:text-white transition-colors"
        title="Dismiss"
      >
        <XMarkIcon className="w-4 h-4" />
      </button>
      <style>{`
        @keyframes broadcast-single {
          0%   { transform: translateX(100vw); }
          100% { transform: translateX(-100%); }
        }
      `}</style>
    </div>
  );
};

// ── Types ─────────────────────────────────────────────────────────────────────

type TransactionType =
  | "deposit"
  | "withdrawal"
  | "trade_earning"
  | "airdrop"
  | "commission"
  | "bot_earning"
  | "referral_earning"
  | "signup_bonus"
  | "signup_reward"
  | "bonus"
  | "bot_purchase"
  | "bot_capital_return"
  | "team_bonus";

interface ActiveBotSubscription {
  id: number;
  subscription_id: number;
  name: string;
  description: string;
  daily_rate: number;
  min_balance: number;
  max_balance: number | null;
  duration_days: number;
  duration_label: string;
  return_capital: boolean;
  color: string;
  total_earnings: number;
  capital_invested: number;
  joined_at: string | null;
  expires_at: string | null;
  days_remaining: number | null;
  image_url: string | null;
}

interface ActiveBotsSummary {
  total_bots: number;
  total_invested: number;
  total_daily_earnings: number;
  total_earnings_so_far: number;
}

interface TradingBot {
  id: number;
  name: string;
  daily_rate: number;
  min_balance: number;
  max_balance: number | null;
  duration_days: number;
  duration_label: string;
  return_capital: boolean;
  color: string;
  estimated_daily_per_1000: number;
  can_join: boolean;
}

// ── Main Component ────────────────────────────────────────────────────────────

const DashboardHome: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const {
    profile,
    kycStatus,
    isLoading: profileLoading,
  } = useSelector((state: RootState) => state.user);
  const {
    balance,
    transactions,
    isLoading: walletLoading,
  } = useSelector((state: RootState) => state.wallet);

  // Multi-bot state
  const [activeBots, setActiveBots] = useState<ActiveBotSubscription[]>([]);
  const [activeSummary, setActiveSummary] = useState<ActiveBotsSummary | null>(
    null,
  );
  const [availableBots, setAvailableBots] = useState<TradingBot[]>([]);

  const [stats, setStats] = useState({
    totalEarnings: 0,
    activeDays: 0,
    totalDeposits: 0,
    totalWithdrawals: 0,
    totalBotEarnings: 0,
    totalBotEarningsXRP: 0,
    totalInvested: 0,
    totalInvestedXRP: 0,
  });

  const [xrpPrice, setXrpPrice] = useState(getXrpPrice());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [tradingLoading, setTradingLoading] = useState(true);
  const [userBalanceXRP, setUserBalanceXRP] = useState<number>(0);
  const [userCreatedAt, setUserCreatedAt] = useState<string | null>(null);

  // Broadcast
  const [broadcast, setBroadcast] = useState<BroadcastData | null>(null);
  const [broadcastDismissed, setBroadcastDismissed] = useState(false);

  // ── Country prompt — uses localStorage so skip is permanent ─────────────
  const [showCountryPrompt, setShowCountryPrompt] = useState(false);

  // ── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (user) loadDashboardData();
    // eslint-disable-next-line
  }, [user]);

  // Broadcast
  useEffect(() => {
    if (!user) return;
    const fetchBroadcast = async () => {
      try {
        const res = await api.get("/dashboard/broadcast");
        if (res.data?.is_active && res.data?.message?.trim()) {
          setBroadcast(res.data as BroadcastData);
        }
      } catch (err) {
        console.warn("[Broadcast] fetch failed:", err);
      }
    };
    fetchBroadcast();
  }, [user]);

  // Country prompt — show once if country not set and not already dismissed
  useEffect(() => {
    if (!user) return;

    const userCountry = (user as any).country || (profile as any)?.country;

    // Only show if user doesn't have a country set
    if (!userCountry) {
      // Show modal after a short delay
      const timer = setTimeout(() => setShowCountryPrompt(true), 900);
      return () => clearTimeout(timer);
    }
  }, [user, profile]);

  useEffect(() => {
    if (profile?.created_at) setUserCreatedAt(profile.created_at);
    else if (user?.created_at) setUserCreatedAt(user.created_at);
    // eslint-disable-next-line
  }, [user, profile]);

  useEffect(() => {
    if (balance?.estimated_usd && user?.xrp_balance && user.xrp_balance > 0) {
      const calc = balance.estimated_usd / user.xrp_balance;
      if (calc > 0) {
        setXrpPriceInUSD(calc);
        setXrpPrice(getXrpPrice());
      }
    }
  }, [balance, user]);

  useEffect(() => {
    calculateStats();
    // eslint-disable-next-line
  }, [
    transactions,
    activeBots,
    activeSummary,
    userBalanceXRP,
    userCreatedAt,
    xrpPrice,
  ]);

  // ── Derived helpers ───────────────────────────────────────────────────────

  const calculateActiveDays = (): number => {
    if (!userCreatedAt) return 0;
    try {
      const diff = Math.abs(
        new Date().getTime() - new Date(userCreatedAt).getTime(),
      );
      return Math.max(1, Math.floor(diff / (1000 * 60 * 60 * 24)));
    } catch {
      return 0;
    }
  };

  const getCurrentXrpBalance = (): number => {
    if (balance?.xrp_balance !== undefined)
      return parseFloat(balance.xrp_balance.toString()) || 0;
    if (userBalanceXRP > 0) return userBalanceXRP;
    return parseFloat(user?.xrp_balance?.toString() || "0") || 0;
  };

  const getTotalDailyEarningsXRP = (): number =>
    activeSummary?.total_daily_earnings ??
    activeBots.reduce(
      (sum, sub) => sum + (sub.capital_invested || 0) * (sub.daily_rate / 100),
      0,
    );

  const getTotalDailyEarningsUSD = (): number =>
    getTotalDailyEarningsXRP() * xrpPrice;
  const getTotalBalanceValue = (): number => getCurrentXrpBalance() * xrpPrice;

  const getNextDistributionTime = () => {
    const now = new Date();
    const tomorrow = new Date(
      Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() + 1,
        0,
        0,
        0,
      ),
    );
    const diffMs = tomorrow.getTime() - now.getTime();
    return {
      hours: Math.floor(diffMs / (1000 * 60 * 60)),
      minutes: Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60)),
    };
  };

  const calculateMonthlyReturn = (dailyRate: number): number =>
    (Math.pow(1 + dailyRate / 100, 30) - 1) * 100;

  const getKycBadge = () => {
    const s = kycStatus || user?.kyc_status;
    switch (s) {
      case "approved":
        return { color: "success", text: "Verified", icon: CheckCircleIcon };
      case "pending":
        return { color: "warning", text: "Pending", icon: ClockIcon };
      case "rejected":
        return {
          color: "danger",
          text: "Rejected",
          icon: ExclamationCircleIcon,
        };
      default:
        return {
          color: "warning",
          text: "Not Submitted",
          icon: ExclamationCircleIcon,
        };
    }
  };

  const formatUserCreatedDate = (): string => {
    const d = profile?.created_at || user?.created_at;
    return d ? formatDate(d, false) : "N/A";
  };

  // ── Data fetching ─────────────────────────────────────────────────────────

  const fetchTradingData = async () => {
    try {
      const botsRes = await api.get("/trading/bots");
      if (botsRes.data?.bots) setAvailableBots(botsRes.data.bots);
      else setAvailableBots([]);

      const activeRes = await tradingBotService.getActiveBots();
      const data = activeRes?.data || activeRes;
      setActiveBots(Array.isArray(data?.bots) ? data.bots : []);
      setActiveSummary(data?.summary ?? null);
    } catch {
      setAvailableBots([]);
      setActiveBots([]);
      setActiveSummary(null);
    }
  };

  const fetchUserBalance = async (): Promise<number> => {
    try {
      const res = await api.get("/wallet/balance");
      return parseFloat(res.data?.xrp_balance) || 0;
    } catch {
      return 0;
    }
  };

  const loadAllTradingData = async (isRefresh = false) => {
    try {
      if (isRefresh) setIsRefreshing(true);
      setTradingLoading(true);
      await fetchTradingData();
      const bal = await fetchUserBalance();
      setUserBalanceXRP(bal);
    } catch (error) {
      console.error("Failed to load trading data:", error);
    } finally {
      setTradingLoading(false);
      if (isRefresh) setIsRefreshing(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      setIsRefreshing(true);
      setTradingLoading(true);
      await Promise.all([
        dispatch(getProfile()),
        dispatch(getBalance()),
        dispatch(getTransactions()),
      ]);
      await loadAllTradingData();
    } catch (error) {
      console.error("Failed to load dashboard:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // ── Stats ─────────────────────────────────────────────────────────────────

  const calculateStats = () => {
    if (!transactions) return;

    const completed = (type: TransactionType) =>
      transactions
        .filter((tx) => tx.type === type && tx.status === "completed")
        .reduce((s, tx) => s + (parseFloat(tx.amount?.toString()) || 0), 0);

    const earningTypes: TransactionType[] = [
      "bot_earning",
      "trade_earning",
      "commission",
      "referral_earning",
      "team_bonus",
      "bonus",
      "signup_bonus",
      "signup_reward",
    ];

    const totalBotEarningsXRP = transactions
      .filter(
        (tx) => earningTypes.includes(tx.type) && tx.status === "completed",
      )
      .reduce((s, tx) => s + (parseFloat(tx.amount?.toString()) || 0), 0);

    const totalInvestedXRP = completed("bot_purchase");

    setStats({
      totalEarnings: totalBotEarningsXRP * xrpPrice,
      activeDays: calculateActiveDays(),
      totalDeposits: completed("deposit") * xrpPrice,
      totalWithdrawals: completed("withdrawal") * xrpPrice,
      totalBotEarnings: totalBotEarningsXRP * xrpPrice,
      totalBotEarningsXRP,
      totalInvested: totalInvestedXRP * xrpPrice,
      totalInvestedXRP,
    });
  };

  // ── Transaction helpers ───────────────────────────────────────────────────

  const formatTransactionType = (type: TransactionType): string =>
    ({
      deposit: "Deposit",
      withdrawal: "Withdrawal",
      trade_earning: "Trading Earnings",
      airdrop: "Airdrop",
      commission: "Commission",
      bot_earning: "Bot Earnings",
      referral_earning: "Referral Earnings",
      signup_bonus: "Signup Bonus",
      signup_reward: "Signup Reward",
      bonus: "Bonus",
      bot_purchase: "Bot Investment",
      bot_capital_return: "Capital Return",
      team_bonus: "Team Bonus",
    })[type] ?? type;

  const getTransactionIcon = (type: TransactionType): JSX.Element => {
    const icons: Record<TransactionType, JSX.Element> = {
      deposit: <ArrowUpIcon className="w-4 h-4 text-green-500" />,
      withdrawal: <ArrowDownIcon className="w-4 h-4 text-red-500" />,
      trade_earning: <ChartBarIcon className="w-4 h-4 text-blue-500" />,
      airdrop: <GiftIcon className="w-4 h-4 text-purple-500" />,
      commission: <BanknotesIcon className="w-4 h-4 text-yellow-500" />,
      bot_earning: <CpuChipIcon className="w-4 h-4 text-cyan-500" />,
      referral_earning: <UserGroupIcon className="w-4 h-4 text-orange-500" />,
      signup_bonus: <SparklesIcon className="w-4 h-4 text-pink-500" />,
      signup_reward: <SparklesIcon className="w-4 h-4 text-pink-500" />,
      bonus: <GiftIcon className="w-4 h-4 text-emerald-500" />,
      bot_purchase: <RocketLaunchIcon className="w-4 h-4 text-indigo-500" />,
      bot_capital_return: <ShieldCheckIcon className="w-4 h-4 text-teal-500" />,
      team_bonus: <TrophyIcon className="w-4 h-4 text-yellow-400" />,
    };
    return icons[type] ?? <SparklesIcon className="w-4 h-4 text-gray-400" />;
  };

  // ── Derived booleans ──────────────────────────────────────────────────────

  const hasActiveBots = activeBots.length > 0;
  const kycBadge = getKycBadge();
  const BadgeIcon = kycBadge.icon;
  const isLoading = profileLoading || walletLoading || isRefreshing;
  const availableToJoin = availableBots.filter((b) => b.can_join).length;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className={globalStyle.container}>
      <Header />

      <main className="page-content2">
        <div className="page-content2-bg" />
        <div className="page-content2-wrapper">
          <div className="page-content2-content">
            {/* Broadcast Marquee */}
            {broadcast && !broadcastDismissed && (
              <div className="mb-4 -mx-4 md:-mx-6 lg:-mx-8 rounded-lg overflow-hidden">
                <BroadcastMarquee
                  data={broadcast}
                  onDismiss={() => setBroadcastDismissed(true)}
                />
              </div>
            )}

            {/* Welcome */}
            <div className="mb-6 md:mb-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h1 className={globalStyle.h3}>
                    {isLoading
                      ? "Loading..."
                      : `Welcome back, ${user?.first_name || user?.username || "User"}!`}
                  </h1>
                  <p className={globalStyle.body2}>
                    Real-time investment dashboard • 1 XRP ={" "}
                    {formatCurrency(xrpPrice)}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div
                    className={`${globalStyle.badge} ${
                      kycBadge.color === "success"
                        ? globalStyle.badgeSuccess
                        : kycBadge.color === "warning"
                          ? globalStyle.badgeWarning
                          : kycBadge.color === "danger"
                            ? globalStyle.badgeDanger
                            : globalStyle.badgeInfo
                    }`}
                  >
                    <BadgeIcon className="w-3 h-3 mr-1 text-gray-800" />
                    {kycBadge.text}
                  </div>
                  {kycStatus !== "approved" && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => navigate("/kyc")}
                    >
                      Complete KYC
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Balance Card */}
            <Card className="mb-6 bg-gradient-to-r from-blue-900 to-purple-900">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="mb-4 md:mb-0">
                  <p className={`${globalStyle.body2} text-primary-100 mb-2`}>
                    Total Balance
                  </p>
                  <div className="flex items-baseline">
                    <h2 className={`${globalStyle.h2} text-white`}>
                      {isLoading || tradingLoading
                        ? "Loading..."
                        : `${formatXRP(getCurrentXrpBalance())} XRP`}
                    </h2>
                    <span className="ml-2 text-primary-200">
                      {formatCurrency(getTotalBalanceValue())}
                    </span>
                  </div>
                  <div className="flex items-center mt-2">
                    <p
                      className={`${globalStyle.caption} text-primary-200 mr-2`}
                    >
                      1 XRP = {formatCurrency(xrpPrice)}
                    </p>
                    <span className="text-xs text-green-300">(Live)</span>
                  </div>
                </div>
                <div className="flex justify-center space-x-3">
                  <Button
                    variant="secondary"
                    size="lg"
                    onClick={() => navigate("/wallet")}
                  >
                    <ArrowUpIcon className="w-4 h-4 mr-2 text-primary" />{" "}
                    Deposit
                  </Button>
                  <Button
                    variant="secondary"
                    size="lg"
                    onClick={() => navigate("/wallet")}
                  >
                    <ArrowDownIcon className="w-4 h-4 mr-2 text-primary" />{" "}
                    Withdraw
                  </Button>
                </div>
              </div>
            </Card>

            {/* Stats Grid */}
            <div className={globalStyle.grid4}>
              {/* Daily Earnings */}
              <Card hover padding="lg">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-lg bg-green-900 flex items-center justify-center mr-4">
                    <FireIcon className="w-6 h-6 text-green-300" />
                  </div>
                  <div>
                    <p className={globalStyle.caption}>Daily Earnings</p>
                    {hasActiveBots ? (
                      <>
                        <h3 className={globalStyle.h4}>
                          {tradingLoading || isRefreshing
                            ? "..."
                            : formatCurrency(getTotalDailyEarningsUSD())}
                        </h3>
                        <p className={`${globalStyle.caption} text-gray-400`}>
                          {tradingLoading || isRefreshing
                            ? "..."
                            : `${formatXRP(getTotalDailyEarningsXRP())} XRP`}
                        </p>
                        {activeBots.length > 1 && (
                          <p
                            className={`${globalStyle.caption} text-green-400 text-xs mt-1`}
                          >
                            Across {activeBots.length} bots
                          </p>
                        )}
                        {activeBots.length === 1 && (
                          <p
                            className={`${globalStyle.caption} text-green-400 text-xs mt-1`}
                          >
                            {formatNumber(activeBots[0].daily_rate, 4)}% •{" "}
                            {formatXRP(activeBots[0].capital_invested)} XRP
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <h3 className={globalStyle.h4}>$0.00</h3>
                        <p className={`${globalStyle.caption} text-gray-400`}>
                          0.00 XRP
                        </p>
                        <p
                          className={`${globalStyle.caption} text-green-400 text-xs mt-1`}
                        >
                          No active bots
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </Card>

              {/* Total Earnings */}
              <Card hover padding="lg">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-lg bg-blue-900 flex items-center justify-center mr-4">
                    <ChartBarIcon className="w-6 h-6 text-blue-300" />
                  </div>
                  <div>
                    <p className={globalStyle.caption}>Total Earnings</p>
                    <h3 className={globalStyle.h4}>
                      {formatCurrency(stats.totalBotEarnings)}
                    </h3>
                    <p className={`${globalStyle.caption} text-gray-400`}>
                      {formatXRP(stats.totalBotEarningsXRP)} XRP
                    </p>
                  </div>
                </div>
              </Card>

              {/* Total Invested */}
              <Card hover padding="lg">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-lg bg-purple-900 flex items-center justify-center mr-4">
                    <RocketLaunchIcon className="w-6 h-6 text-purple-300" />
                  </div>
                  <div>
                    <p className={globalStyle.caption}>Total Invested</p>
                    <h3 className={globalStyle.h4}>
                      {formatCurrency(stats.totalInvested)}
                    </h3>
                    <p className={`${globalStyle.caption} text-gray-400`}>
                      {formatXRP(stats.totalInvestedXRP)} XRP
                    </p>
                    {activeSummary && activeSummary.total_bots > 0 && (
                      <p
                        className={`${globalStyle.caption} text-purple-400 text-xs mt-1`}
                      >
                        Live: {formatXRP(activeSummary.total_invested)} XRP (
                        {activeSummary.total_bots} bot
                        {activeSummary.total_bots > 1 ? "s" : ""})
                      </p>
                    )}
                  </div>
                </div>
              </Card>

              {/* Active Days */}
              <Card hover padding="lg">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-lg bg-orange-900 flex items-center justify-center mr-4">
                    <ArrowsRightLeftIcon className="w-6 h-6 text-orange-300" />
                  </div>
                  <div>
                    <p className={globalStyle.caption}>Active Days</p>
                    <h3 className={globalStyle.h4}>
                      {stats.activeDays === 0 && !isLoading
                        ? "1 day"
                        : `${stats.activeDays} day(s)`}
                    </h3>
                    <p className={`${globalStyle.caption} text-gray-400`}>
                      Since {formatUserCreatedDate()}
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Trading Status + Recent Transactions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mt-6 md:mt-8">
              {/* Trading Bot Status */}
              <Card padding="lg">
                <div className="flex items-center justify-between mb-4">
                  <h3 className={globalStyle.h5}>Trading Bot Status</h3>
                  <div className="flex items-center space-x-2">
                    {hasActiveBots && (
                      <>
                        <div className="w-2 h-2 rounded-full animate-pulse bg-green-500" />
                        <span
                          className={`${globalStyle.badge} ${globalStyle.badgeSuccess}`}
                        >
                          {activeBots.length} Active
                        </span>
                      </>
                    )}
                    <Button
                      variant="tertiary"
                      size="sm"
                      onClick={() => loadAllTradingData(true)}
                      loading={isRefreshing}
                    >
                      <ArrowPathIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="mt-4 space-y-4">
                  {tradingLoading ? (
                    <LoadingScreen message="Loading trading status..." />
                  ) : hasActiveBots ? (
                    <>
                      {activeBots.length > 1 && activeSummary && (
                        <div className="bg-green-900/20 border border-green-800/40 rounded-lg p-3 mb-2">
                          <p className="text-xs font-semibold text-green-300 mb-2">
                            Portfolio Summary — {activeSummary.total_bots}{" "}
                            Active Bots
                          </p>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <p
                                className={`${globalStyle.caption} text-gray-400`}
                              >
                                Total Invested
                              </p>
                              <p className="font-medium text-white">
                                {formatXRP(activeSummary.total_invested)} XRP
                              </p>
                            </div>
                            <div>
                              <p
                                className={`${globalStyle.caption} text-gray-400`}
                              >
                                Daily (all bots)
                              </p>
                              <p className="font-medium text-green-400">
                                +{formatXRP(activeSummary.total_daily_earnings)}{" "}
                                XRP
                              </p>
                            </div>
                            <div>
                              <p
                                className={`${globalStyle.caption} text-gray-400`}
                              >
                                Total Earned
                              </p>
                              <p className="font-medium text-green-400">
                                {formatXRP(activeSummary.total_earnings_so_far)}{" "}
                                XRP
                              </p>
                            </div>
                            <div>
                              <p
                                className={`${globalStyle.caption} text-gray-400`}
                              >
                                Est. USD/day
                              </p>
                              <p className="font-medium text-white">
                                {formatCurrency(
                                  activeSummary.total_daily_earnings * xrpPrice,
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {activeBots.map((sub, idx) => {
                        const dailyEarnings =
                          (sub.capital_invested || 0) * (sub.daily_rate / 100);
                        return (
                          <div
                            key={sub.subscription_id}
                            className={`space-y-2 ${idx < activeBots.length - 1 ? "pb-3 border-b border-gray-800" : ""}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <div
                                  className="w-8 h-8 rounded-lg flex items-center justify-center mr-2"
                                  style={{ backgroundColor: `${sub.color}20` }}
                                >
                                  <RocketLaunchIcon
                                    className="w-4 h-4"
                                    style={{ color: sub.color }}
                                  />
                                </div>
                                <div>
                                  <span className={globalStyle.body2}>
                                    {sub.name}
                                  </span>
                                  <p
                                    className={`${globalStyle.caption} text-gray-400`}
                                  >
                                    {formatNumber(sub.daily_rate, 4)}% daily
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <span className="font-bold text-green-500">
                                  +{formatXRP(dailyEarnings)} XRP
                                </span>
                                <p
                                  className={`${globalStyle.caption} text-gray-400`}
                                >
                                  per day
                                </p>
                              </div>
                            </div>
                            <div className="ml-10 space-y-1">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-400">Invested</span>
                                <span className="text-white font-medium">
                                  {formatXRP(sub.capital_invested)} XRP{" "}
                                  <span className="text-gray-400 ml-1">
                                    (
                                    {formatCurrency(
                                      sub.capital_invested * xrpPrice,
                                    )}
                                    )
                                  </span>
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-400">
                                  Earned so far
                                </span>
                                <span className="text-green-400 font-medium">
                                  {formatXRP(sub.total_earnings)} XRP
                                </span>
                              </div>
                              {sub.capital_invested > 0 &&
                                sub.total_earnings >= 0 && (
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-400">ROI</span>
                                    <span className="text-green-400 font-medium">
                                      {formatNumber(
                                        (sub.total_earnings /
                                          sub.capital_invested) *
                                          100,
                                        2,
                                      )}
                                      %
                                    </span>
                                  </div>
                                )}
                              {sub.return_capital && (
                                <div className="flex items-center gap-1 text-xs text-blue-400">
                                  <ShieldCheckIcon className="w-3 h-3" />
                                  Capital returns on expiry
                                </div>
                              )}
                              {sub.expires_at && (
                                <div className="flex items-center gap-1 text-xs text-gray-400">
                                  <CalendarIcon className="w-3 h-3" />
                                  Expires{" "}
                                  {new Date(
                                    sub.expires_at,
                                  ).toLocaleDateString()}
                                  {sub.days_remaining !== null && (
                                    <span
                                      className={`ml-1 font-semibold ${sub.days_remaining <= 1 ? "text-red-400" : sub.days_remaining <= 3 ? "text-yellow-400" : "text-green-400"}`}
                                    >
                                      ({sub.days_remaining}d left)
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      <div className="flex items-center justify-between pt-2 border-t border-gray-800">
                        <div className="flex items-center">
                          <ClockIcon className="w-4 h-4 text-purple-500 mr-2" />
                          <span className={globalStyle.body2}>
                            Next Distribution
                          </span>
                        </div>
                        <div className="text-right">
                          <span className="font-medium">00:00 UTC</span>
                          <p className={`${globalStyle.caption} text-gray-400`}>
                            {(() => {
                              const { hours, minutes } =
                                getNextDistributionTime();
                              return `${hours}h ${minutes}m`;
                            })()}
                          </p>
                        </div>
                      </div>
                    </>
                  ) : availableBots.length > 0 ? (
                    <>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <InformationCircleIcon className="w-4 h-4 text-blue-500 mr-2" />
                          <span className={globalStyle.body2}>
                            Available Bots
                          </span>
                        </div>
                        <div className="text-right">
                          <span className="font-bold">
                            {availableBots.length}
                          </span>
                          <p className={`${globalStyle.caption} text-gray-400`}>
                            Ready to join
                          </p>
                        </div>
                      </div>
                      <div className="bg-gray-800 rounded-lg p-3">
                        <div className="flex items-center mb-2">
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center mr-2"
                            style={{
                              backgroundColor: `${availableBots[0].color}20`,
                            }}
                          >
                            <RocketLaunchIcon
                              className="w-4 h-4"
                              style={{ color: availableBots[0].color }}
                            />
                          </div>
                          <div>
                            <span className="font-medium">
                              {availableBots[0].name}
                            </span>
                            <p
                              className={`${globalStyle.caption} text-gray-400`}
                            >
                              Recommended
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <div>
                            <p
                              className={`${globalStyle.caption} text-gray-400`}
                            >
                              Daily Rate
                            </p>
                            <p className="font-medium text-green-500">
                              {formatNumber(availableBots[0].daily_rate, 4)}%
                            </p>
                          </div>
                          <div>
                            <p
                              className={`${globalStyle.caption} text-gray-400`}
                            >
                              Per 1000 XRP
                            </p>
                            <p className="font-medium text-green-500">
                              +
                              {formatXRP(
                                availableBots[0].estimated_daily_per_1000,
                              )}{" "}
                              XRP
                            </p>
                          </div>
                          <div>
                            <p
                              className={`${globalStyle.caption} text-gray-400`}
                            >
                              Range
                            </p>
                            <p className="font-medium">
                              {formatXRP(availableBots[0].min_balance)} –{" "}
                              {availableBots[0].max_balance !== null
                                ? formatXRP(availableBots[0].max_balance)
                                : "∞"}{" "}
                              XRP
                            </p>
                          </div>
                          <div>
                            <p
                              className={`${globalStyle.caption} text-gray-400`}
                            >
                              Duration
                            </p>
                            <p className="font-medium">
                              {availableBots[0].duration_label}
                            </p>
                          </div>
                          <div>
                            <p
                              className={`${globalStyle.caption} text-gray-400`}
                            >
                              Capital Return
                            </p>
                            <p
                              className={`font-medium ${availableBots[0].return_capital ? "text-blue-400" : "text-gray-400"}`}
                            >
                              {availableBots[0].return_capital ? "Yes" : "No"}
                            </p>
                          </div>
                          <div>
                            <p
                              className={`${globalStyle.caption} text-gray-400`}
                            >
                              Monthly Return
                            </p>
                            <p className="font-medium text-blue-400">
                              {formatNumber(
                                calculateMonthlyReturn(
                                  availableBots[0].daily_rate,
                                ),
                                2,
                              )}
                              %
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className={globalStyle.body2}>Can Join</span>
                        <span
                          className={`${globalStyle.badge} ${availableToJoin > 0 ? globalStyle.badgeSuccess : globalStyle.badgeDanger}`}
                        >
                          {availableToJoin} bot(s)
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <ExclamationCircleIcon className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <p className={globalStyle.body2}>
                        No trading bots available
                      </p>
                      <Button
                        variant="tertiary"
                        size="sm"
                        className="mt-2"
                        onClick={() => loadAllTradingData(true)}
                      >
                        Retry
                      </Button>
                    </div>
                  )}
                </div>

                <Button
                  fullWidth
                  className="mt-6"
                  onClick={() => navigate("/trading-bots")}
                >
                  <RocketLaunchIcon className="w-5 h-5 mr-2" />
                  {hasActiveBots ? "Manage Trading Bots" : "Join Trading Bot"}
                </Button>
              </Card>

              {/* Recent Transactions */}
              <Card padding="lg">
                <div className="flex items-center justify-between mb-4">
                  <h3 className={globalStyle.h5}>Recent Transactions</h3>
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => navigate("/wallet")}
                  >
                    View All
                  </Button>
                </div>
                <div className="space-y-3">
                  {isLoading ? (
                    <LoadingScreen message="Loading transactions..." />
                  ) : transactions && transactions.length > 0 ? (
                    transactions.slice(0, 5).map((tx) => (
                      <div
                        key={tx.id}
                        className="flex items-center justify-between py-3 border-b border-gray-800 last:border-0"
                      >
                        <div className="flex items-center">
                          {getTransactionIcon(tx.type)}
                          <div className="ml-3">
                            <p className="font-medium text-sm">
                              {formatTransactionType(tx.type)}
                            </p>
                            <p className={globalStyle.caption}>
                              {new Date(tx.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p
                            className={`font-medium ${tx.type === "withdrawal" || tx.type === "bot_purchase" ? "text-red-500" : "text-green-500"}`}
                          >
                            {tx.type === "withdrawal" ||
                            tx.type === "bot_purchase"
                              ? "-"
                              : "+"}
                            {formatXRP(parseFloat(tx.amount?.toString()) || 0)}{" "}
                            XRP
                          </p>
                          <p className={`${globalStyle.caption} text-gray-400`}>
                            {formatCurrency(
                              (parseFloat(tx.amount?.toString()) || 0) *
                                xrpPrice,
                            )}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <p className={globalStyle.body2}>
                        No recent transactions
                      </p>
                      <Button
                        variant="tertiary"
                        size="sm"
                        className="mt-2"
                        onClick={() => navigate("/wallet")}
                      >
                        Make Your First Transaction
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* Quick Actions */}
            <div className="mt-6 md:mt-8">
              <h3 className={`${globalStyle.h5} mb-4`}>Quick Actions</h3>
              <div className={globalStyle.grid4}>
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => navigate("/wallet")}
                >
                  <ArrowUpIcon className="w-4 h-4 mr-2 text-primary" /> Deposit
                </Button>
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => navigate("/wallet")}
                >
                  <ArrowDownIcon className="w-4 h-4 mr-2 text-primary" />{" "}
                  Withdraw
                </Button>
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => navigate("/trading-bots")}
                >
                  <RocketLaunchIcon className="w-4 h-4 mr-2 text-primary" />{" "}
                  Trading Bots
                </Button>
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => navigate("/airdrops")}
                >
                  <ShieldCheckIcon className="w-4 h-4 mr-2 text-primary" />{" "}
                  Airdrops
                </Button>
              </div>
            </div>

            {/* Refresh */}
            <div className="mt-6 flex justify-center">
              <Button
                variant="primary"
                onClick={loadDashboardData}
                loading={isRefreshing}
              >
                <ArrowTrendingUpIcon className="w-5 h-5 mr-2" />
                Refresh All Data
              </Button>
            </div>

            {/* Active bot info banners */}
            {hasActiveBots && (
              <div className="mt-5 space-y-3">
                {activeBots.map((sub) => {
                  const dailyEarnings =
                    (sub.capital_invested || 0) * (sub.daily_rate / 100);
                  return (
                    <Card key={sub.subscription_id} className="border-l-0">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                        <div className="mb-4 md:mb-0">
                          <div className="flex items-center mb-2">
                            <div
                              className="w-8 h-8 rounded-lg flex items-center justify-center mr-2"
                              style={{ backgroundColor: `${sub.color}20` }}
                            >
                              <RocketLaunchIcon
                                className="w-4 h-4"
                                style={{ color: sub.color }}
                              />
                            </div>
                            <h4 className={`${globalStyle.h6} text-green-300`}>
                              {sub.name} Bot Active
                            </h4>
                          </div>
                          <p className={`${globalStyle.body2} text-gray-300`}>
                            Your investment of {formatXRP(sub.capital_invested)}{" "}
                            XRP is earning {formatNumber(sub.daily_rate, 4)}%
                            daily (+{formatXRP(dailyEarnings)} XRP/day)
                          </p>
                          <p
                            className={`${globalStyle.caption} text-gray-400 mt-1`}
                          >
                            Range: {formatXRP(sub.min_balance)} –{" "}
                            {sub.max_balance !== null
                              ? formatXRP(sub.max_balance)
                              : "∞"}{" "}
                            XRP • Duration: {sub.duration_label}
                            {sub.return_capital &&
                              " • Capital returns on expiry"}{" "}
                            • Earned: {formatXRP(sub.total_earnings)} XRP
                            {sub.capital_invested > 0 &&
                              ` • ROI: ${formatNumber((sub.total_earnings / sub.capital_invested) * 100, 2)}%`}{" "}
                            • Next distribution at 00:00 UTC •{" "}
                            {(() => {
                              const { hours, minutes } =
                                getNextDistributionTime();
                              return `${hours}h ${minutes}m remaining`;
                            })()}
                          </p>
                        </div>
                        <Button
                          variant="primary"
                          onClick={() => navigate("/trading-bots")}
                          style={{ backgroundColor: sub.color }}
                          className="hover:opacity-90 text-white"
                        >
                          Manage
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Join bot banner */}
            {!hasActiveBots && availableBots.length > 0 && (
              <Card className="mt-6 bg-gradient-to-r from-blue-900 to-indigo-900 border-blue-800">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                  <div className="mb-4 md:mb-0">
                    <div className="flex items-center mb-2">
                      <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse mr-2" />
                      <h4 className={`${globalStyle.h6} text-blue-300`}>
                        Join a Trading Bot
                      </h4>
                    </div>
                    <p className={`${globalStyle.body2} text-blue-200`}>
                      You have {availableToJoin} trading bot(s) available to
                      join. Invest any amount between min and max to start
                      earning daily returns!
                    </p>
                    <p className={`${globalStyle.caption} text-blue-300 mt-1`}>
                      Top recommendation: {availableBots[0]?.name} •{" "}
                      {formatNumber(availableBots[0]?.daily_rate || 0, 4)}%
                      daily •{" "}
                      {formatXRP(
                        availableBots[0]?.estimated_daily_per_1000 || 0,
                      )}{" "}
                      XRP/1000 XRP •{" "}
                      {availableBots[0]?.max_balance !== null
                        ? `Up to ${formatXRP(availableBots[0].max_balance!)} XRP`
                        : "Unlimited balance"}{" "}
                      • Duration: {availableBots[0]?.duration_label} •{" "}
                      {availableBots[0]?.return_capital
                        ? "Capital returns"
                        : "No capital return"}
                    </p>
                  </div>
                  <Button
                    variant="primary"
                    onClick={() => navigate("/trading-bots")}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <PlusCircleIcon className="w-5 h-5 mr-2" />
                    View Bots
                  </Button>
                </div>
              </Card>
            )}
          </div>
        </div>
      </main>

      <BottomNav />

      {/* Country prompt — shown once for users without a country set */}
      <CountryPromptModal
        isOpen={showCountryPrompt}
        onClose={() => setShowCountryPrompt(false)}
      />
    </div>
  );
};

export default DashboardHome;
